import React, { useState, useEffect } from "react"
import { useNavigate } from "@tanstack/react-router"
import { useAdminLogin, useCurrentAdmin } from "@/lib/queries"
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { SsoButton } from "@/components/SsoButton"

export function AdminLoginPage() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const navigate = useNavigate()
  const login = useAdminLogin()
  const { data: admin, isLoading } = useCurrentAdmin()

  const searchParams = new URLSearchParams(window.location.search)
  const redirectTo = searchParams.get("redirect") || "/admin"
  const hasRedirect = searchParams.has("redirect")
  const ssoError = searchParams.get("sso_error")
    ? "We couldn't sign you in with single sign-on. Use your email and password, or contact your administrator."
    : null

  // If already authenticated (e.g. session still valid), skip login
  useEffect(() => {
    if (!isLoading && admin) {
      navigate({ to: redirectTo })
    }
  }, [isLoading, admin, navigate, redirectTo])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    login.mutate({ email, password }, {
      onSuccess: () => navigate({ to: redirectTo }),
    })
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-col items-center text-center">
          <img src="/signature_logo.png" alt="Specia" className="h-10 mb-2" />
          <CardDescription>Sign in to the admin portal</CardDescription>
        </CardHeader>
        <CardContent>
          {ssoError && (
            <p className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive text-center">
              {ssoError}
            </p>
          )}
          {hasRedirect && (
            <p className="mb-4 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              Please sign in to access this page.
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {login.isError && (
              <p className="text-sm text-destructive">{login.error instanceof Error ? login.error.message : "Login failed"}</p>
            )}
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          <div className="my-4 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            or
            <span className="h-px flex-1 bg-border" />
          </div>
          <div className="space-y-2">
            <SsoButton label="Sign in with Google" path="/auth/google_oauth2?from=admin" />
            <SsoButton label="Sign in with Microsoft" path="/auth/entra_id?from=admin" />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
